/**
 * canvas-engine/item-contract/itemThemeResolver.js
 *
 * SINGLE SOURCE OF TRUTH for the Theme_Cascade resolver shared by the Canva-style
 * canvas builder (`admin-console-ui`) and the published-page reader (`academy-ui`,
 * via the synced `canvas-engine/` mirror). It replaces the two hand-copies that
 * previously drifted — the builder's `modules/canvas-builder/theme/themeEngine.js`
 * and the reader's `template/sections/canvas/canvasThemeResolver.js` — which become
 * thin re-exports of this module (design tasks 6.1 / 11.1). Because both roles call
 * the SAME resolver, they cannot disagree about an effective theme value (Req 11.5).
 *
 * It resolves each effective themeable value using the precedence
 * `Item_Style override → Section_Theme → Template_Theme` (first defined level wins,
 * Req 11.1), treating a property as "defined" at a level when that level owns the key
 * with a value that is neither `undefined` nor `null` — the empty string `''` counts
 * as defined (Req 11.2). When no level defines a property, `resolveEffective` returns
 * `undefined` (Req 11.4).
 *
 * ZERO-REUSE / ZERO-SIDE-EFFECT CONSTRAINT: imports ONLY the shared constants from
 * `./itemConstants.js` — no React, no DOM, nothing from
 * `admin-console-ui/src/modules/template-builder/` or `academy-ui/src/template/`.
 * Every export takes plain objects and returns either a primitive value or a NEW plain
 * object, never mutating its inputs, so results serialize, mirror, and round-trip
 * cleanly and are importable by the non-React Parity_Verifier. The resolver is TOTAL:
 * it never throws for any input.
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4 (item color overrides + cascade fallback),
 * 9.2, 9.3, 9.4 (Template_Theme as base), 9.5, 9.6 (DEFAULT_TEMPLATE_THEME default
 * base), 10.2, 10.3 (Section_Theme participation), 11.1–11.5 (cascade precedence,
 * defines semantics, skip/undefined, parity), 11.6 (exactly the themeable key set).
 * Design: Components §2 (Theme_Resolver); Data Models (Theme shapes & resolution);
 * Properties 6 (cascade parity), 7 (exact themeable key set).
 */

import { THEMEABLE, DEFAULT_TEMPLATE_THEME } from './itemConstants.js';

// ── Internal helpers ───────────────────────────────────────────────────────

/**
 * A level "defines" a property when it is a non-null object that owns the key with a
 * value that is neither `undefined` nor `null`. A present string (including `''`) is
 * considered defined; only `undefined`/`null`/missing count as "not defined".
 * (Identical to the legacy `themeEngine.defines` / `canvasThemeResolver.defines`.)
 *
 * @param {*} level    the candidate cascade level (any value; non-objects are "not defined")
 * @param {string} property  the themeable key to test
 * @returns {boolean} true when `level` owns `property` with a non-undefined/non-null value
 */
function defines(level, property) {
  if (level == null || typeof level !== 'object') return false;
  const value = level[property];
  return value !== undefined && value !== null;
}

/**
 * Extract the Section_Theme object from a `section` regardless of which shape is
 * passed. The builder's in-process model carries the theme directly on `section.theme`
 * (see `canvasModel.createSection`), while a raw persisted section nests it under
 * `section.canvas.theme` (the Canvas_Payload shape). Prefer the in-process `section.theme`
 * when present; otherwise fall back to the persisted `section.canvas.theme`. Returns
 * `undefined` when neither exists, so the cascade simply skips the Section_Theme level.
 *
 * @param {*} section  a builder Section, a parsed reader model, or a persisted section
 * @returns {object|undefined} the Section_Theme overrides, or `undefined`
 */
function sectionThemeOf(section) {
  if (section == null || typeof section !== 'object') return undefined;
  if (section.theme != null && typeof section.theme === 'object') return section.theme;
  const canvas = section.canvas;
  if (canvas != null && typeof canvas === 'object' && canvas.theme != null && typeof canvas.theme === 'object') {
    return canvas.theme;
  }
  return undefined;
}

/**
 * Build the effective Template_Theme base used by `resolveItemStyle`. The
 * Template_Theme is the always-present base of the cascade, so when the passed
 * `templateTheme` is missing, empty, or omits a themeable property, the resolver falls
 * back to `DEFAULT_TEMPLATE_THEME` for that property (Req 9.5 / 9.6). This guarantees
 * the builder and reader resolve the same value even when `template.theme` is absent
 * or partial. NOTE: this default-base behavior is intentionally applied only by
 * `resolveItemStyle`; the lower-level `resolveEffective` stays faithful to the raw
 * cascade (Req 11.4) and never injects defaults.
 *
 * @param {*} templateTheme  the persisted/in-process Template_Theme (may be absent/partial)
 * @returns {{ textColor: *, backgroundColor: *, fontFamily: * }} a fully-populated base
 */
function effectiveTemplateTheme(templateTheme) {
  const base = {};
  for (const property of THEMEABLE) {
    base[property] = defines(templateTheme, property)
      ? templateTheme[property]
      : DEFAULT_TEMPLATE_THEME[property];
  }
  return base;
}

// ── Resolution ───────────────────────────────────────────────────────────────

/**
 * Resolve the effective value of a single themeable `property` using the precedence
 * order Item_Style override → Section_Theme → Template_Theme, returning the value from
 * the FIRST level that defines it (Req 11.1; Property 6). A level that does not exist or
 * does not define the property is skipped (Req 11.3). When NO level defines the
 * property, returns `undefined` (Req 11.4). This is the raw cascade — it does NOT inject
 * `DEFAULT_TEMPLATE_THEME`; default-base behavior belongs to `resolveItemStyle`.
 *
 * @param {string} property         one of THEMEABLE (`textColor`/`backgroundColor`/`fontFamily`)
 * @param {object} [itemOverride]   sparse item-level overrides (item.style)
 * @param {object} [sectionTheme]   sparse Section_Theme overrides (section.theme)
 * @param {object} [templateTheme]  the Template_Theme base
 * @returns {*} the first defined value, or `undefined` if no level defines it
 */
export function resolveEffective(property, itemOverride, sectionTheme, templateTheme) {
  if (defines(itemOverride, property)) return itemOverride[property]; // Req 8.1/8.2, 11.1 (1st)
  if (defines(sectionTheme, property)) return sectionTheme[property]; // Req 10.2, 11.1 (2nd)
  if (defines(templateTheme, property)) return templateTheme[property]; // Req 9.2/9.3, 11.1 (3rd)
  return undefined; // Req 11.4
}

/**
 * Resolve the full effective style for an item by resolving every THEMEABLE property
 * through the cascade (Req 8.3, 9.4, 10.3, 11.1). The item's overrides come from
 * `item.style`, the Section_Theme from `section.theme` (or the persisted
 * `section.canvas.theme`), and the base from `templateTheme` — backed by
 * `DEFAULT_TEMPLATE_THEME` whenever `templateTheme` is missing/empty/partial so a value
 * is always produced for every themeable property (Req 9.5 / 9.6).
 *
 * Returns a NEW plain object whose keys are EXACTLY `textColor`, `backgroundColor`,
 * `fontFamily` — no more, no fewer (Req 11.6; Property 7) — and never mutates its inputs.
 *
 * @param {object} [item]           the item whose `style` carries overrides
 * @param {object} [section]        the section whose `theme`/`canvas.theme` carries overrides
 * @param {object} [templateTheme]  the Template_Theme base (may be absent/partial)
 * @returns {{ textColor: *, backgroundColor: *, fontFamily: * }} resolved style
 */
export function resolveItemStyle(item, section, templateTheme) {
  const itemOverride = item == null ? undefined : item.style;
  const sectionTheme = sectionThemeOf(section);
  const baseTheme = effectiveTemplateTheme(templateTheme);
  return {
    textColor: resolveEffective('textColor', itemOverride, sectionTheme, baseTheme),
    backgroundColor: resolveEffective('backgroundColor', itemOverride, sectionTheme, baseTheme),
    fontFamily: resolveEffective('fontFamily', itemOverride, sectionTheme, baseTheme),
  };
}

// ── Theme mutation helpers (pure; never mutate inputs) ─────────────────────────

/**
 * Seed a NEW Section_Theme object by copying every THEMEABLE value that the
 * `templateTheme` defines (builder authoring helper; Req 10). The result equals the
 * template theme for all themeable keys, so a freshly-seeded section resolves
 * identically to the template until the author overrides a value. Undefined/null
 * template values are skipped so the seeded object never introduces "not defined"
 * placeholders. (Transcribed from `themeEngine.seedSectionTheme`.)
 *
 * @param {object} [templateTheme] the Template_Theme to copy from
 * @returns {object} a new Section_Theme object
 */
export function seedSectionTheme(templateTheme) {
  const seeded = {};
  for (const property of THEMEABLE) {
    if (defines(templateTheme, property)) {
      seeded[property] = templateTheme[property];
    }
  }
  return seeded;
}

/**
 * Return a shallow COPY of `themeObj` with `property` removed (reset-to-inherit),
 * never mutating the input (builder reset helper). After removal the property is
 * "not defined" at this level, so `resolveEffective` resolves it from the parent level.
 * (Transcribed from `themeEngine.withoutOverride`.)
 *
 * @param {object} [themeObj] the theme object to copy
 * @param {string} property   the key to remove
 * @returns {object} a new theme object without `property`
 */
export function withoutOverride(themeObj, property) {
  const next = { ...(themeObj || {}) };
  delete next[property];
  return next;
}
